'use client';

/**
 * RevenueSharePanel — record the reimbursement an org collected in a month and
 * see the revenue-share fee it produces.
 *
 * The fee is calculated from the org's plan (e.g. 12% of reimbursement on
 * "Care Coordination + Reimbursement") by POST /revenue-share/calculate, and
 * appears as a line on the org's next invoice for that period. A record can be
 * deleted until it has been invoiced.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Percent, Trash2 } from 'lucide-react';

interface Api {
  get: <T = any>(path: string) => Promise<T | null>;
  post: <T = any>(path: string, body: any) => Promise<T>;
  del: <T = any>(path: string) => Promise<T>;
}

interface ShareRecord {
  id: string;
  period_start: string;
  period_end: string;
  reimbursement_revenue: number;
  grant_revenue: number;
  total_revenue: number;
  final_amount: number;
  minimum_applied: boolean;
  cap_applied: boolean;
  status: string;
  notes: string | null;
}

const money = (n: any) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const lastMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const STATUS_STYLE: Record<string, string> = {
  calculated: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  invoiced: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  paid: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300',
};
const STATUS_LABEL: Record<string, string> = {
  calculated: 'Not yet invoiced',
  invoiced: 'Invoiced',
  paid: 'Paid',
  pending: 'Pending',
  disputed: 'Disputed',
};

export default function RevenueSharePanel({
  cboId,
  api,
  sharePercent,
}: {
  cboId: number;
  api: Api;
  sharePercent?: number | null;
}) {
  const [records, setRecords] = useState<ShareRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState(lastMonth());
  const [reimbursement, setReimbursement] = useState('');
  const [grant, setGrant] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api.get<{ records: ShareRecord[] }>(`/revenue-share/records?cbo_id=${cboId}&page_size=24`);
    setRecords(res?.records || []);
    setLoading(false);
  }, [api, cboId]);

  useEffect(() => {
    setNotice(null);
    setError(null);
    load();
  }, [load]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const amount = Number(reimbursement);
    if (!month || !(amount >= 0) || reimbursement === '') {
      setError('Enter the month and the reimbursement collected.');
      return;
    }
    const [y, m] = month.split('-').map(Number);
    const start = `${month}-01`;
    const end = new Date(y, m, 0).toISOString().slice(0, 10);
    setSaving(true);
    setError(null);
    try {
      const res = await api.post<any>('/revenue-share/calculate', {
        cbo_id: cboId,
        period_start: start,
        period_end: end,
        reimbursement_revenue: amount,
        grant_revenue: grant ? Number(grant) : 0,
        notes: notes || null,
      });
      setNotice(
        `Recorded. Revenue share for ${month}: ${money(res.final_amount)}` +
          (res.minimum_applied ? ' (monthly minimum applied)' : '') +
          (res.cap_applied ? ' (monthly cap applied)' : '') +
          '. It will appear on the next invoice for that month.',
      );
      setReimbursement('');
      setGrant('');
      setNotes('');
      await load();
    } catch (err: any) {
      setError(err.message || 'Could not record revenue');
    } finally {
      setSaving(false);
    }
  };

  const fromRemittances = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await api.post<any>('/revenue-share/calculate-from-remittances', { cbo_id: cboId, month });
      if (res.result === 'created' || res.result === 'updated') {
        setNotice(`From paid claims for ${month}: ${money(res.collected)} collected on ${res.claims} claim(s) → revenue share ${money(res.fee)}. It will appear on the next invoice for that month.`);
      } else {
        setNotice(`Nothing recorded for ${month}: ${res.result}.`);
      }
      await load();
    } catch (err: any) {
      setError(err.message || 'Could not calculate from paid claims');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    setConfirmDelete(null);
    setError(null);
    try {
      await api.del(`/revenue-share/records/${id}`);
      setNotice('Record deleted.');
      await load();
    } catch (err: any) {
      setError(err.message || 'Could not delete the record');
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Percent className="h-4 w-4" /> Revenue share
        </h3>
        <p className="text-xs text-gray-500 mt-0.5 max-w-2xl">
          Record the reimbursement this organization collected in a month, or calculate it from the payer remittances
          (835 / ERA) the platform has received. The fee is calculated
          from its plan{sharePercent ? ` (${sharePercent}% of reimbursement)` : ''} and billed on the next invoice for that month.
        </p>
      </div>

      <form onSubmit={submit} className="flex flex-wrap items-end gap-3 text-sm">
        <label>
          <span className="block text-xs text-gray-500 mb-1">Month</span>
          <input
            id="rev-share-month"
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900"
          />
        </label>
        <label>
          <span className="block text-xs text-gray-500 mb-1">Reimbursement collected ($)</span>
          <input
            id="rev-share-reimbursement"
            type="number"
            min="0"
            step="0.01"
            value={reimbursement}
            onChange={(e) => setReimbursement(e.target.value)}
            placeholder="0.00"
            className="w-40 px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900"
          />
        </label>
        <label>
          <span className="block text-xs text-gray-500 mb-1">Grant revenue ($, optional)</span>
          <input
            id="rev-share-grant"
            type="number"
            min="0"
            step="0.01"
            value={grant}
            onChange={(e) => setGrant(e.target.value)}
            placeholder="0.00"
            className="w-36 px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900"
          />
        </label>
        <label className="flex-1 min-w-[12rem]">
          <span className="block text-xs text-gray-500 mb-1">Note (e.g. remittance reference)</span>
          <input
            id="rev-share-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900"
          />
        </label>
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:bg-gray-400"
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Record revenue
        </button>
        <button
          type="button"
          onClick={fromRemittances}
          disabled={saving}
          title="Sum what this organization's payers actually paid in the month (835 / ERA remittances) and calculate the fee"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg disabled:opacity-50 dark:bg-indigo-900/30 dark:text-indigo-300"
        >
          Calculate from paid claims
        </button>
      </form>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {notice && !error && <p className="text-sm text-green-700 dark:text-green-400">{notice}</p>}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading records…</div>
      ) : records.length === 0 ? (
        <p className="text-sm text-gray-500">No revenue recorded for this organization yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="text-xs text-gray-500 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="text-left py-2 pr-3 font-medium">Month</th>
                <th className="text-right py-2 pr-3 font-medium">Revenue recorded</th>
                <th className="text-right py-2 pr-3 font-medium">Revenue share</th>
                <th className="text-left py-2 pr-3 font-medium">Status</th>
                <th className="text-left py-2 pr-3 font-medium">Note</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {records.map((r) => (
                <tr key={r.id}>
                  <td className="py-2 pr-3">
                    {new Date(r.period_start + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">{money(r.total_revenue)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums font-medium">
                    {money(r.final_amount)}
                    {r.minimum_applied && <span className="ml-1 text-xs text-gray-500">(min)</span>}
                    {r.cap_applied && <span className="ml-1 text-xs text-gray-500">(cap)</span>}
                  </td>
                  <td className="py-2 pr-3">
                    <span className={`px-2 py-0.5 rounded text-xs ${STATUS_STYLE[r.status] || 'bg-gray-100 text-gray-600'}`}>
                      {STATUS_LABEL[r.status] || r.status}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-gray-500 max-w-[16rem] truncate">{r.notes || ''}</td>
                  <td className="py-2 text-right text-xs">
                    {['calculated', 'pending'].includes(r.status) &&
                      (confirmDelete === r.id ? (
                        <span className="inline-flex gap-2">
                          <button onClick={() => remove(r.id)} className="text-red-600 font-medium hover:underline">Confirm delete</button>
                          <button onClick={() => setConfirmDelete(null)} className="text-gray-500 hover:underline">Cancel</button>
                        </span>
                      ) : (
                        <button onClick={() => setConfirmDelete(r.id)} className="inline-flex items-center gap-1 text-gray-500 hover:text-red-600">
                          <Trash2 className="h-3 w-3" /> Delete
                        </button>
                      ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
